import React, { useState, useRef, useEffect, useMemo } from "react";
import {
  Send,
  Mic,
  Plus,
  Bot,
  ThumbsUp,
  ThumbsDown,
  Copy,
  X,
  Edit3,
} from "lucide-react";
import PlotDisplay from "./PlotDisplay";
import MarkdownRenderer from "./MarkdownRenderer";
import StructuredTableRenderer from "./StructuredTableRenderer";
import ReportHandoff from "./ReportHandoff";
import ClarifyCard from "./ClarifyCard";
import ArtifactList from "./ArtifactList";
import { useAuth } from "../contexts/AuthContext";
import { API_ENDPOINTS } from "../config/api";
import { createConversation, getConversation, sendFeedback } from "../utils/conversationsApi";
import * as chatRunStore from "../utils/chatRunStore";

// Map a server MessageOut row to the UI message shape used throughout this view.
// `meta` rides through whole (never key-filtered), so everything the final stream
// event attached to the turn - report_request / report_candidates, clarify,
// artifacts - is there again after a reload. Assets and plots come back too when
// the server persisted them; a turn without them keeps the empty defaults.
const mapServerMessage = (m) => ({
  id: m.id,
  serverId: m.id,
  role: m.role,
  content: m.content || "",
  responseMeta: m.meta || {},
  structuredTable: (m.meta && m.meta.structured_table) || {},
  assets: (m.meta && m.meta.assets) || {},
  plots: (m.meta && m.meta.plots) || [],
  feedback: m.feedback ?? 0,
});

const ChatInterface = ({
  darkMode,
  conversationId = null,
  onConversationCreated = null,
  onConversationUpdated = null,
}) => {
  const { fetchWithAuth, recordActivity } = useAuth();

  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [thinkingTime, setThinkingTime] = useState(0);
  const [isThinking, setIsThinking] = useState(false);
  const [lastThinkingTime, setLastThinkingTime] = useState(null);
  const messagesEndRef = useRef(null);
  const thinkingIntervalRef = useRef(null);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);
  const [editingMessage, setEditingMessage] = useState(null);
  // Optimistic thumbs feedback overlay, keyed by server message id. The effective
  // vote for a message is override ?? msg.feedback ?? 0.
  const [feedbackOverrides, setFeedbackOverrides] = useState({});
  const [attachments, setAttachments] = useState([]);
  const [showAttachmentPreview, setShowAttachmentPreview] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recognition, setRecognition] = useState(null);
  const [interimTranscript, setInterimTranscript] = useState('');
  const [recordingStartTime, setRecordingStartTime] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const wantsToRecordRef = useRef(false); // Track if user wants to continue recording
  const silenceTimerRef = useRef(null); // Ref to persist timer across recognition restarts

  // Track the latest assistant message id to attach the "Thought for" chip next to the nametag
  const lastAssistantId = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i]?.role === 'assistant') return messages[i].id;
    }
    return null;
  }, [messages]);

  const messagesContainerRef = useRef(null);
  const userScrolledUpRef = useRef(false);

  const scrollToBottom = () => {
    if (userScrolledUpRef.current) return;
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleMessagesScroll = () => {
    const el = messagesContainerRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    userScrolledUpRef.current = distanceFromBottom > 80;
  };

  const scrollRafRef = useRef(null);
  useEffect(() => {
    if (scrollRafRef.current) cancelAnimationFrame(scrollRafRef.current);
    scrollRafRef.current = requestAnimationFrame(scrollToBottom);
    return () => { if (scrollRafRef.current) cancelAnimationFrame(scrollRafRef.current); };
  }, [messages]);

  // Subscribe to the persistent chat-run store for this conversation AND hydrate
  // its messages from the server (history now survives hard refresh + re-login).
  // A conversationId of null is a new unsaved chat: show the empty state; the
  // conversation is created lazily on first send. If a run is already in flight
  // for this conversation we do NOT re-load from the server (never clobber a
  // live stream); the subscription reflects it.
  useEffect(() => {
    if (!conversationId) {
      setMessages([]);
      setIsLoading(false);
      setIsThinking(false);
      setFeedbackOverrides({});
      return undefined;
    }
    const unsubscribe = chatRunStore.subscribe(
      conversationId,
      ({ messages: nextMessages, running, elapsed }) => {
        setMessages(nextMessages);
        setIsLoading(running);
        setIsThinking(running);
        if (!running && elapsed != null) setLastThinkingTime(elapsed);
      }
    );
    let cancelled = false;
    if (!chatRunStore.isRunning(conversationId)) {
      setFeedbackOverrides({});
      getConversation(fetchWithAuth, conversationId)
        .then((detail) => {
          if (cancelled || chatRunStore.isRunning(conversationId)) return;
          chatRunStore.seedMessages(conversationId, (detail.messages || []).map(mapServerMessage));
        })
        .catch(() => {
          // fail-soft: an unreachable server leaves the (empty) run in place.
        });
    }
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [conversationId, fetchWithAuth]);

  useEffect(() => {
    if (isThinking) {
      thinkingIntervalRef.current = setInterval(() => {
        setThinkingTime(prev => prev + 1);
      }, 1000);
    } else {
      if (thinkingIntervalRef.current) {
        clearInterval(thinkingIntervalRef.current);
        thinkingIntervalRef.current = null;
      }
      setThinkingTime(0);
    }

    return () => {
      if (thinkingIntervalRef.current) {
        clearInterval(thinkingIntervalRef.current);
      }
    };
  }, [isThinking]);

  // Handle click-away for editing mode
  useEffect(() => {
    const onDocClick = (e) => {
      if (editingMessage && !e.target.closest('.editing-message-container')) {
        setEditingMessage(null);
        setMessage("");
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [editingMessage]);

  // Update recording time display
  useEffect(() => {
    let interval = null;
    if (isRecording && recordingStartTime) {
      interval = setInterval(() => {
        setRecordingTime(Math.floor((Date.now() - recordingStartTime) / 1000));
      }, 1000);
    } else {
      setRecordingTime(0);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isRecording, recordingStartTime]);

  // Auto-resize textarea — runs only when content changes, not on every render
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
    }
  }, [message, interimTranscript]);

  // The agent run loop (status / streaming / finalize / error handling) lives in
  // ../utils/chatRunStore so it survives this component unmounting on navigation.
  // handleSendMessage delegates to chatRunStore.startRun; the subscription effect
  // above renders whatever the store emits.

  const extractStructuredSummary = (msg) => {
    if (msg?.responseMeta?.summary) return msg.responseMeta.summary;

    const content = msg?.content || "";
    const lines = content
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean);

    for (const line of lines) {
      if (
        line.startsWith("|") ||
        /^[-|:\s]+$/.test(line) ||
        line.includes("----") ||
        line.toLowerCase().startsWith("account name |")
      ) {
        continue;
      }
      return line.replace(/^#+\s*/, "");
    }

    return "";
  };

  const getPostTableContent = (content) => {
    if (!content) return '';
    const lines = content.split('\n');
    let inTable = false;
    let tableEnded = false;
    const postLines = [];

    for (const line of lines) {
      if (tableEnded) {
        postLines.push(line);
        continue;
      }
      const trimmed = line.trim();
      if (trimmed.startsWith('|') && trimmed.endsWith('|')) {
        inTable = true;
      } else if (/^[-|:\s]+$/.test(trimmed) && inTable) {
        // table separator row
      } else if (inTable) {
        tableEnded = true;
        if (trimmed) postLines.push(line);
      }
    }

    if (!inTable) {
      let skippedFirst = false;
      const fallback = [];
      for (const line of lines) {
        if (!skippedFirst && line.trim()) {
          skippedFirst = true;
          continue;
        }
        if (skippedFirst) fallback.push(line);
      }
      return fallback.join('\n').trim();
    }

    return postLines.join('\n').trim();
  };

  const getAssistantContainerClass = (msg) => {
    if (msg?.structuredTable && msg.structuredTable.row_count > 0) {
      return "w-full max-w-[min(96vw,1400px)]";
    }
    return "max-w-[75ch]";
  };

  // `overrideText` lets a UI affordance (e.g. the report handoff's "Other"
  // option) send a specific query. Existing onClick handlers pass a MouseEvent,
  // so anything non-string falls back to the composer's `message` state.
  const handleSendMessage = async (overrideText) => {
    const isOverride = typeof overrideText === "string";
    const raw = isOverride ? overrideText : message;
    if (!raw.trim() || isLoading) return;
    userScrolledUpRef.current = false;

    const trimmedMessage = raw.trim();
    const userMessage = {
      id: Date.now(),
      role: "user",
      content: trimmedMessage,
      timestamp: new Date(),
      attachments: attachments.length > 0 ? attachments.map(a => ({
        id: a.id,
        name: a.name,
        type: a.type,
        size: a.size
      })) : []
    };

    // When editing, replace the edited message in place; otherwise append.
    const baseMessages = (!isOverride && editingMessage)
      ? messages.map(msg =>
          msg.id === editingMessage
            ? { ...msg, content: trimmedMessage, timestamp: new Date() }
            : msg
        )
      : [...messages, userMessage];
    if (!isOverride && editingMessage) setEditingMessage(null);

    setMessages(baseMessages);
    if (!isOverride) setMessage("");
    setAttachments([]);
    setIsLoading(true);
    setIsThinking(true);
    recordActivity();

    // Lazily create the conversation on the FIRST send of a new chat, then pass
    // its server id into the stream so the completed turn is persisted.
    let convId = conversationId;
    try {
      if (!convId) {
        const created = await createConversation(fetchWithAuth);
        convId = created.id;
      }
    } catch (e) {
      console.error("Failed to create conversation:", e);
      setMessages([
        ...baseMessages,
        { id: Date.now() + 1, role: "assistant", content: "Sorry, I couldn't start this chat. Please try again.", timestamp: new Date() },
      ]);
      setIsLoading(false);
      setIsThinking(false);
      return;
    }

    chatRunStore.startRun(convId, {
      fetchWithAuth,
      endpoint: API_ENDPOINTS.chatStream,
      body: {
        message: trimmedMessage,
        conversation_id: convId,
      },
      baseMessages,
      recordActivity,
      onAssistant: () => {
        if (typeof onConversationUpdated === 'function') onConversationUpdated(convId);
      },
    });

    // Switch the sidebar/selection to the newly created conversation.
    if (!conversationId && typeof onConversationCreated === 'function') {
      onConversationCreated(convId);
    }
  };

  const handleFeedback = async (msg, vote) => {
    if (!msg.serverId) return; // disabled until the turn is persisted
    const current = feedbackOverrides[msg.serverId] ?? msg.feedback ?? 0;
    const next = current === vote ? 0 : vote; // toggle-to-clear
    setFeedbackOverrides((o) => ({ ...o, [msg.serverId]: next }));
    try {
      await sendFeedback(fetchWithAuth, msg.serverId, next);
    } catch (e) {
      setFeedbackOverrides((o) => ({ ...o, [msg.serverId]: current })); // revert
    }
  };

  const effectiveFeedback = (msg) => feedbackOverrides[msg.serverId] ?? msg.feedback ?? 0;

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatSecondsInt = (seconds) => Math.max(0, Math.floor(seconds));

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const handleFileAttachment = async (event) => {
    const files = Array.from(event.target.files);
    const newAttachments = await Promise.all(
      files.map(async (file) => {
        const id = Date.now() + Math.random();
        let url = URL.createObjectURL(file);
        let dataUrl = null;

        // Convert images to base64 for persistence
        if (file.type.startsWith('image/')) {
          try {
            dataUrl = await new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => resolve(reader.result);
              reader.onerror = reject;
              reader.readAsDataURL(file);
            });
          } catch (e) {
            console.error('Failed to convert image to base64:', e);
          }
        }

        return {
          id,
          file,
          name: file.name,
          type: file.type,
          size: file.size,
          url, // Keep blob URL for immediate display
          dataUrl // Store base64 for persistence
        };
      })
    );
    setAttachments(prev => [...prev, ...newAttachments]);
  };

  const removeAttachment = (attachmentId) => {
    setAttachments(prev => {
      const attachment = prev.find(a => a.id === attachmentId);
      if (attachment) {
        URL.revokeObjectURL(attachment.url);
      }
      return prev.filter(a => a.id !== attachmentId);
    });
  };

  const getFileIcon = (fileType) => {
    if (fileType.startsWith('image/')) return '🖼️';
    if (fileType.includes('pdf')) return '📄';
    if (fileType.includes('word') || fileType.includes('document')) return '📝';
    if (fileType.includes('excel') || fileType.includes('spreadsheet')) return '📊';
    if (fileType.includes('powerpoint') || fileType.includes('presentation')) return '📽️';
    if (fileType.includes('text')) return '📄';
    return '📎';
  };

  // Helper function to add punctuation to speech transcript
  const addPunctuation = (text, isFinal) => {
    if (!text || text.trim() === '') return '';

    let processed = text.trim();

    // Capitalize first letter
    processed = processed.charAt(0).toUpperCase() + processed.slice(1);

    // Question words that indicate a question
    const questionStarters = ['what', 'when', 'where', 'why', 'who', 'how', 'is', 'are', 'was', 'were', 'do', 'does', 'did', 'can', 'could', 'would', 'should', 'will', 'have', 'has', 'am'];
    const firstWord = processed.split(' ')[0].toLowerCase();
    const isQuestion = questionStarters.includes(firstWord);

    // Only add ending punctuation for final results
    if (isFinal) {
      // Remove any trailing spaces
      processed = processed.trimEnd();

      // Don't add punctuation if it already ends with one
      if (!/[.!?]$/.test(processed)) {
        if (isQuestion) {
          processed += '?';
        } else {
          processed += '.';
        }
      }
    }

    return processed;
  };

  // Initialize speech recognition - simplified version
  useEffect(() => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognitionInstance = new SpeechRecognition();

    // Simple settings
    recognitionInstance.continuous = false; // Stop after each phrase for reliability
    recognitionInstance.interimResults = true;
    recognitionInstance.lang = 'en-US';

    // Track last audio input time for auto-stop after 3 seconds of no audio
    let lastAudioTime = null;

    const resetSilenceTimer = () => {
      // Clear existing timer
      if (silenceTimerRef.current) {
        clearTimeout(silenceTimerRef.current);
        silenceTimerRef.current = null;
      }

      // Start new 3-second timer
      if (wantsToRecordRef.current) {
        silenceTimerRef.current = setTimeout(() => {
          if (wantsToRecordRef.current) {
            wantsToRecordRef.current = false;
            try {
              recognitionInstance.stop();
            } catch (e) {
              // ignore
            }
            setIsRecording(false);
            clearSilenceTimer();
          }
        }, 3000);
      }
    };

    const clearSilenceTimer = () => {
      if (silenceTimerRef.current) {
        clearTimeout(silenceTimerRef.current);
        silenceTimerRef.current = null;
      }
    };

    recognitionInstance.onstart = () => {
      setIsRecording(true);
      setRecordingStartTime(Date.now());
    };

    recognitionInstance.onresult = (event) => {
      let hasFinalResult = false;

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        const transcript = result[0].transcript;
        const confidence = result[0].confidence || 0;

        if (result.isFinal) {
          hasFinalResult = true;
          lastAudioTime = Date.now();
          clearSilenceTimer();

          const trimmedTranscript = transcript.trim();
          const isNoiseWord = ['uh', 'um', 'ah', 'eh', 'oh', 'hmm', 'mm'].includes(trimmedTranscript.toLowerCase());
          const isMeaningful = confidence > 0.5 && trimmedTranscript.length >= 2 && !isNoiseWord;

          if (isMeaningful) {
            const punctuated = addPunctuation(transcript, true);
            setMessage(prev => {
              const prevTrimmed = prev.trimEnd();
              if (prevTrimmed && !prevTrimmed.endsWith(' ')) {
                return prevTrimmed + ' ' + punctuated + ' ';
              }
              return prev + punctuated + ' ';
            });
            setInterimTranscript('');
          } else {
            setInterimTranscript('');
          }
        } else {
          const trimmedTranscript = transcript.trim();
          const isNoiseWord = ['uh', 'um', 'ah', 'eh', 'oh', 'hmm', 'mm'].includes(trimmedTranscript.toLowerCase());
          const isMeaningful = confidence > 0.5 && trimmedTranscript.length >= 2 && !isNoiseWord;

          if (isMeaningful) {
            setInterimTranscript(addPunctuation(transcript, false));
          }
        }
      }

      if (hasFinalResult) {
        resetSilenceTimer();
      }
    };

    recognitionInstance.onerror = (event) => {
      if (event.error === 'not-allowed') {
        alert('Microphone access denied. Please allow microphone permissions.');
        clearSilenceTimer();
        wantsToRecordRef.current = false;
        setIsRecording(false);
      } else if (event.error === 'audio-capture') {
        alert('No microphone found. Please check your microphone.');
        clearSilenceTimer();
        wantsToRecordRef.current = false;
        setIsRecording(false);
      }
    };

    recognitionInstance.onend = () => {
      setInterimTranscript('');

      // Auto-restart if user still wants to record AND timer hasn't fired yet
      if (wantsToRecordRef.current) {
        setTimeout(() => {
          if (wantsToRecordRef.current) {
            try {
              recognitionInstance.start();
            } catch (e) {
              setIsRecording(false);
              wantsToRecordRef.current = false;
              clearSilenceTimer();
            }
          }
        }, 100);
      } else {
        setIsRecording(false);
        clearSilenceTimer();
        lastAudioTime = null;
      }
    };

    recognitionInstance.onspeechend = () => {
      if (lastAudioTime) {
        resetSilenceTimer();
      }
    };

    recognitionInstance.onaudioend = () => {
      if (lastAudioTime) {
        resetSilenceTimer();
      }
    };

    setRecognition(recognitionInstance);

    // Cleanup function
    return () => {
      clearSilenceTimer();
    };
  }, []);

  const startVoiceRecording = () => {
    if (!recognition) {
      alert('Speech recognition is not supported in your browser. Please use Chrome or Edge.');
      return;
    }

    if (isRecording) {
      return;
    }

    try {
      wantsToRecordRef.current = true;
      recognition.start();
    } catch (error) {
      wantsToRecordRef.current = false;
    }
  };

  const stopVoiceRecording = () => {
    wantsToRecordRef.current = false; // User wants to stop
    if (recognition && isRecording) {
      try {
        recognition.stop();
        setIsRecording(false);
      } catch (error) {
        // ignore
      }
    }
  };

  return (
    <div className="flex flex-col h-full relative overflow-visible">
      {/* Chat Messages Area */}
      <div ref={messagesContainerRef} onScroll={handleMessagesScroll} className={`flex-1 overflow-y-auto custom-scrollbar p-6 ${darkMode ? 'bg-[#0A0A0A]' : 'bg-white'}`}>
        {messages.length === 0 ? (
          <div style={{
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center',
            height: '100%', textAlign: 'center', padding: '0 24px',
          }}>
            {/* Heading */}
            <h2
              className="font-display"
              style={{
                fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)',
                fontWeight: 400,
                letterSpacing: '-0.025em',
                lineHeight: 1.15,
                color: darkMode ? '#FFFFFF' : '#0A0A0A',
                margin: 0,
                maxWidth: 480,
              }}
            >
              Ask a question or<br />ask for a report
            </h2>
          </div>
        ) : (
          <div className="w-full flex justify-center">
            <div className="w-full max-w-[min(96vw,1500px)]">
              <div className="space-y-7">
                {messages.map((msg) => (
                  <div key={msg.id} className={`w-full ${msg.role === 'user' ? 'relative group' : ''}`}>
                    {msg.role === 'user' ? (
                      <div className="flex justify-end">
                        <div className="max-w-[75ch]">
                          <div className={`text-xs mb-1 text-right ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>You</div>
                          {editingMessage === msg.id ? (
                            <div className="flex items-center gap-2 editing-message-container">
                              <textarea
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    handleSendMessage();
                                  }
                                  if (e.key === 'Escape') {
                                    setEditingMessage(null);
                                    setMessage("");
                                  }
                                }}
                                className={`flex-1 px-4 py-3 rounded-2xl border resize-none focus:outline-none focus:ring-2 focus:ring-white/40 ${
                                  darkMode
                                    ? 'bg-[#141414] border-white/25 text-white placeholder-white/35'
                                    : 'bg-white border-yellow-500 text-gray-900 placeholder-gray-500'
                                }`}
                                rows={1}
                                style={{ minHeight: '48px', maxHeight: '120px' }}
                                autoFocus
                              />
                              <button
                                onClick={() => {
                                  setEditingMessage(null);
                                  setMessage("");
                                }}
                                className={`p-2 rounded-lg transition-colors ${
                                  darkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-200 text-gray-500'
                                }`}
                                title="Cancel editing"
                              >
                                <X className="w-4 h-4" />
                              </button>
                              <button
                                onClick={handleSendMessage}
                                disabled={!message.trim() || isLoading}
                                className={`p-2 rounded-lg transition-colors ${
                                  message.trim() && !isLoading
                                    ? 'bg-white text-[#0A0A0A] hover:bg-white/90'
                                    : darkMode
                                    ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
                                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                }`}
                                title="Send edited message"
                              >
                                <Send className="w-4 h-4" />
                              </button>
                            </div>
                          ) : (
                            <>
                              <div className={`inline-block px-4 py-3 rounded-2xl border ${darkMode ? 'bg-[#141414] border-gray-700 text-gray-100' : 'bg-gray-100 border-gray-300 text-gray-900'}`}>
                                <div className="whitespace-pre-wrap leading-7">{msg.content}</div>
                              </div>
                              <div className="flex justify-end gap-1 mt-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                                <button
                                  onClick={() => copyToClipboard(msg.content)}
                                  className={`p-1.5 rounded-md transition-colors ${darkMode ? 'hover:bg-gray-700 text-gray-400 hover:text-gray-200' : 'hover:bg-gray-200 text-gray-500 hover:text-gray-700'}`}
                                  title="Copy message"
                                >
                                  <Copy className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => {
                                    setEditingMessage(msg.id);
                                    setMessage(msg.content);
                                  }}
                                  className={`p-1.5 rounded-md transition-colors ${darkMode ? 'hover:bg-gray-700 text-gray-400 hover:text-gray-200' : 'hover:bg-gray-200 text-gray-500 hover:text-gray-700'}`}
                                  title="Edit message"
                                >
                                  <Edit3 className="w-4 h-4" />
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    ) : msg.role === 'status' ? (
                      <div className="flex justify-start">
                        <div className="max-w-[75ch]">
                          <div className="flex items-center gap-2 mb-2">
                            <div className={`w-6 h-6 rounded-md ${darkMode ? 'bg-white/10' : 'bg-black/[.07]'} flex items-center justify-center`}>
                              <Bot className={`w-4 h-4 ${darkMode ? 'text-white' : 'text-gray-700'}`} />
                            </div>
                            <div className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>AI Analyst</div>
                          </div>
                          <div className={`text-sm italic ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            {msg.content}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="flex justify-start">
                        <div className={getAssistantContainerClass(msg)}>
                          <div className="flex items-center gap-2 mb-2">
                            <div className={`w-6 h-6 rounded-md ${darkMode ? 'bg-white/10' : 'bg-black/[.07]'} flex items-center justify-center`}>
                              <Bot className={`w-4 h-4 ${darkMode ? 'text-white' : 'text-gray-700'}`} />
                            </div>
                            <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>AI Analyst</div>
                            {!isThinking && lastThinkingTime !== null && msg.id === lastAssistantId && (
                              <span className={`text-[12px] ml-2 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                                Thought for {formatSecondsInt(lastThinkingTime)}s
                              </span>
                            )}
                          </div>
                          {msg.responseMeta?.title && (
                            <div className={`text-lg font-semibold mb-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                              {msg.responseMeta.title}
                            </div>
                          )}
                          {msg.structuredTable && msg.structuredTable.row_count > 0 ? (
                            <>
                              {extractStructuredSummary(msg) && (
                                <div className={`${darkMode ? 'text-gray-200' : 'text-gray-800'} leading-7 mb-3`}>
                                  {extractStructuredSummary(msg)}
                                </div>
                              )}
                              <StructuredTableRenderer table={msg.structuredTable} darkMode={darkMode} />
                              {(() => {
                                const postTable = getPostTableContent(msg.content);
                                if (!postTable) return null;
                                return (
                                  <div className={`mt-4 ${darkMode ? 'text-gray-200' : 'text-gray-800'} leading-7`}>
                                    <MarkdownRenderer markdown={postTable} assets={msg.assets} darkMode={darkMode} />
                                  </div>
                                );
                              })()}
                            </>
                          ) : (
                            <div className={`${darkMode ? 'text-gray-200' : 'text-gray-800'} leading-7`}>
                              <MarkdownRenderer markdown={msg.content} assets={msg.assets} darkMode={darkMode} />
                              {msg.isStreaming && (
                                <span className={`inline-block w-2 h-5 ml-0.5 ${darkMode ? 'bg-white' : 'bg-gray-900'} rounded-sm animate-pulse align-text-bottom`} />
                              )}
                            </div>
                          )}
                          {/* Chat-to-report handoff: generates + opens the report as a
                              floating window over the chat. A single resolved client shows
                              one button; an ambiguous reference shows a choice card (one per
                              match + Other) that generates on selection. */}
                          <ReportHandoff
                            reportRequest={msg.responseMeta?.report_request}
                            reportCandidates={msg.responseMeta?.report_candidates}
                            darkMode={darkMode}
                            onOther={handleSendMessage}
                            messageId={msg.serverId}
                          />
                          {/* Clarifying questions: answering sends the choices back
                              as a normal chat turn. Only the LATEST assistant message
                              is answerable - an older card's question is stale. */}
                          <ClarifyCard
                            clarify={msg.responseMeta?.clarify}
                            darkMode={darkMode}
                            onSend={handleSendMessage}
                            messageId={msg.serverId}
                            readOnly={msg.id !== lastAssistantId}
                          />
                          {/* Files this answer produced (workbook / memo / chart). */}
                          <ArtifactList artifacts={msg.responseMeta?.artifacts} darkMode={darkMode} />
                          {/* Render plots if present */}
                          {msg.plots && msg.plots.length > 0 && (
                            <PlotDisplay plots={msg.plots} />
                          )}
                          <div className="flex items-center gap-2 mt-2">
                            <button
                              onClick={() => copyToClipboard(msg.content)}
                              className={`p-1 rounded transition-colors ${darkMode ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-200 text-gray-500'}`}
                              title="Copy"
                            >
                              <Copy className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleFeedback(msg, 1)}
                              disabled={!msg.serverId}
                              title={msg.serverId ? 'Good response' : 'Available once the answer is saved'}
                              className={`p-1 rounded transition-colors ${
                                !msg.serverId
                                  ? 'opacity-40 cursor-not-allowed text-gray-500'
                                  : effectiveFeedback(msg) === 1
                                  ? (darkMode ? 'text-green-400 bg-green-400/10' : 'text-green-600 bg-green-100')
                                  : (darkMode ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-200 text-gray-500')
                              }`}
                            >
                              <ThumbsUp className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleFeedback(msg, -1)}
                              disabled={!msg.serverId}
                              title={msg.serverId ? 'Bad response' : 'Available once the answer is saved'}
                              className={`p-1 rounded transition-colors ${
                                !msg.serverId
                                  ? 'opacity-40 cursor-not-allowed text-gray-500'
                                  : effectiveFeedback(msg) === -1
                                  ? (darkMode ? 'text-red-400 bg-red-400/10' : 'text-red-600 bg-red-100')
                                  : (darkMode ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-200 text-gray-500')
                              }`}
                            >
                              <ThumbsDown className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}

                {/* Thinking Animation */}
                {isThinking && (
                  <div className="flex items-center gap-2">
                    <div className={`w-6 h-6 rounded-md ${darkMode ? 'bg-white/10' : 'bg-black/[.07]'} flex items-center justify-center`}>
                      <Bot className={`w-4 h-4 ${darkMode ? 'text-white' : 'text-gray-700'}`} />
                    </div>
                    <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      Thinking for {formatTime(thinkingTime)}...
                    </span>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Input Area - Fixed at bottom */}
      {!editingMessage && (
        <div className={`${darkMode ? 'bg-[#0A0A0A]' : 'bg-gray-50'}`}>
          <div className="pt-4 pb-4 px-4">
            {/* Voice Input Status Indicator */}
            {isRecording && (
              <div className={`mb-5 px-3 py-2 rounded-lg flex items-center gap-2 ${
                darkMode ? 'bg-green-900/20 border border-green-500/30' : 'bg-green-50 border border-green-200'
              }`}>
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className={`text-sm ${darkMode ? 'text-green-300' : 'text-green-600'}`}>
                  {interimTranscript
                    ? 'Processing speech...'
                    : 'Listening for speech... (speak clearly)'}
                </span>
                <span className={`text-xs ml-auto ${darkMode ? 'text-green-400' : 'text-green-500'}`}>
                  {recordingTime}s
                </span>
              </div>
            )}

            <div className="flex justify-center">
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileAttachment}
                className="hidden"
                accept="image/*,.pdf,.doc,.docx,.txt,.xls,.xlsx,.ppt,.pptx"
              />
              <div className={`w-full max-w-2xl border rounded-3xl overflow-hidden transition-all ${darkMode ? 'bg-[#141414] border-white/15' : 'bg-[#F0F0F0] border-black/10'} ${
                attachments.filter(a => a.type.startsWith('image/')).length > 0 ? 'pb-2' : ''
              }`}>
                {/* Upper section for images */}
                {attachments.filter(a => a.type.startsWith('image/')).length > 0 && (
                  <div className="px-3 pt-3 pb-2 flex flex-wrap gap-2">
                    {attachments.filter(a => a.type.startsWith('image/')).map((attachment) => (
                      <div
                        key={attachment.id}
                        className="relative group"
                      >
                        <img
                          src={attachment.url}
                          alt={attachment.name}
                          className="w-20 h-20 object-cover rounded-lg cursor-pointer border border-gray-600"
                          onClick={() => setShowAttachmentPreview(attachment)}
                        />
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            removeAttachment(attachment.id);
                          }}
                          className="absolute -top-2 -right-2 opacity-0 group-hover:opacity-100 transition-opacity w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center hover:bg-red-600 shadow-lg"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Lower section: input bar */}
                <div className="flex items-center gap-1 px-2 py-1.5">
                  {/* Plus button inside bar */}
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    title="Add files and more"
                    className={`p-2 rounded-full transition-colors cursor-pointer flex items-center justify-center self-center focus:outline-none focus:ring-0 ${darkMode ? 'text-gray-400 hover:text-gray-200 hover:bg-white/10' : 'text-gray-500 hover:text-gray-800 hover:bg-black/[.07]'}`}
                  >
                    <Plus className="w-5 h-5" />
                  </button>

                  {/* Text input */}
                  <textarea
                    ref={textareaRef}
                    value={message + (interimTranscript ? ` ${interimTranscript}` : '')}
                    onChange={(e) => setMessage(e.target.value)}
                    onKeyDown={handleKeyPress}
                    placeholder={isRecording ? "Listening..." : "Ask Banyan"}
                    className={`flex-1 px-2 py-2 resize-none focus:outline-none focus:ring-0 outline-none border-none overflow-y-auto rounded-2xl ${darkMode ? 'chat-input-dark' : 'bg-transparent text-gray-900 placeholder-gray-400'}`}
                    rows={1}
                    style={{ minHeight: '40px', maxHeight: '120px' }}
                  />

                  {/* Microphone button inside bar */}
                  <button
                    onClick={isRecording ? stopVoiceRecording : startVoiceRecording}
                    className={`p-2 rounded-full transition-colors flex items-center justify-center self-center focus:outline-none focus:ring-0 ${
                      !recognition
                        ? 'opacity-50 cursor-not-allowed text-gray-500'
                        : isRecording
                          ? 'bg-green-500 text-white animate-pulse shadow-lg shadow-green-500/25'
                          : darkMode ? 'text-gray-400 hover:text-gray-200 hover:bg-white/10' : 'text-gray-500 hover:text-gray-800 hover:bg-black/[.07]'
                    }`}
                    title={
                      !recognition
                        ? 'Voice input not supported in this browser'
                        : isRecording
                          ? 'Stop recording (or wait for 3s silence)'
                          : 'Start voice input'
                    }
                    disabled={!recognition}
                  >
                    <Mic className={`w-5 h-5 ${isRecording ? 'animate-bounce' : ''}`} />
                  </button>

                  {/* Send button inside bar */}
                  <button
                    onClick={handleSendMessage}
                    disabled={!message.trim() || isLoading}
                    className={`p-2.5 rounded-full transition-all duration-200 flex items-center justify-center self-center focus:outline-none focus:ring-0 ${
                      message.trim() && !isLoading
                        ? 'bg-white text-[#0A0A0A] hover:bg-white/90'
                        : 'text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    <Send className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>

            <div className={`text-xs mt-2 text-center ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              AI can make mistakes. Please double check it.
            </div>
          </div>
        </div>
      )}

      {/* Attachment Preview Modal */}
      {showAttachmentPreview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`relative max-w-4xl max-h-4xl p-6 rounded-lg ${
            darkMode ? 'bg-[#141414]' : 'bg-white'
          }`}>
            <button
              onClick={() => setShowAttachmentPreview(null)}
              className={`absolute top-2 right-2 p-2 rounded-lg ${
                darkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'
              }`}
            >
              <X className="w-6 h-6" />
            </button>
            <div className="mb-4">
              <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {showAttachmentPreview.name}
              </h3>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {(showAttachmentPreview.size / 1024).toFixed(1)} KB
              </p>
            </div>
            {showAttachmentPreview.type.startsWith('image/') ? (
              <img
                src={showAttachmentPreview.url}
                alt={showAttachmentPreview.name}
                className="max-w-full max-h-96 object-contain rounded"
              />
            ) : (
              <div className={`flex items-center justify-center p-8 border-2 border-dashed rounded-lg ${
                darkMode ? 'border-gray-600 text-gray-400' : 'border-gray-300 text-gray-500'
              }`}>
                <div className="text-center">
                  <div className="text-6xl mb-4">{getFileIcon(showAttachmentPreview.type)}</div>
                  <p className="text-lg font-medium">{showAttachmentPreview.name}</p>
                  <p className="text-sm">Preview not available for this file type</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatInterface;
